import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';
import { Ship, Map, PartyPopper, Compass, Sparkles, Tag, MapPin, ChevronLeft, Hotel, Car } from 'lucide-react';
import { photoSrc } from '../utils/photo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', icon: Sparkles, label_en: 'All', label_es: 'Todo' },
  { id: 'tours', icon: Map, label_en: 'Tours', label_es: 'Tours' },
  { id: 'yachts', icon: Ship, label_en: 'Yachts', label_es: 'Yates' },
  { id: 'nightlife', icon: PartyPopper, label_en: 'Nightlife', label_es: 'Vida nocturna' },
  { id: 'activities', icon: Compass, label_en: 'Activities', label_es: 'Actividades' },
  { id: 'lodging', icon: Hotel, label_en: 'Lodging', label_es: 'Hospedaje' },
  { id: 'car_rental', icon: Car, label_en: 'Cars', label_es: 'Autos' },
];

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1620694655075-6ed740167a66?auto=format&fit=crop&q=80&w=800';

export default function CatalogPage() {
  const { language } = useLanguage();
  const { format: formatPrice } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const initialCat = searchParams.get('category') || 'all';
  const [category, setCategory] = useState(initialCat);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = category && category !== 'all' ? `?category=${category}` : '';
    axios.get(`${API_URL}/api/products${params}`)
      .then(r => setProducts(r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category]);

  useEffect(() => {
    if (category === 'all') setSearchParams({});
    else setSearchParams({ category });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  return (
    <MobileShell>
      <TopBar
        title={language === 'en' ? 'Catalog' : 'Catálogo'}
        showBrand={false}
        right={
          <Link to="/" data-testid="catalog-back" className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
            <ChevronLeft size={18} />
          </Link>
        }
      />

      {/* Category chips */}
      <div className="px-3 pt-3 pb-2 overflow-x-auto scrollbar-hide flex gap-2 sticky top-14 bg-background/95 backdrop-blur z-30 border-b border-border">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const active = c.id === category;
          return (
            <button
              key={c.id}
              data-testid={`catalog-chip-${c.id}`}
              onClick={() => setCategory(c.id)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold transition-all ${
                active
                  ? 'bg-caribbean text-white shadow-md'
                  : 'bg-muted text-foreground hover:bg-muted/70'
              }`}
            >
              <Icon size={14} />
              {language === 'en' ? c.label_en : c.label_es}
            </button>
          );
        })}
      </div>

      <div className="p-3" data-testid="catalog-grid">
        {loading ? (
          <div className="text-center text-sm text-muted-foreground py-12">
            {language === 'en' ? 'Loading...' : 'Cargando...'}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-12 space-y-2">
            <p>{language === 'en' ? 'No products yet in this category.' : 'Aún no hay productos en esta categoría.'}</p>
            <p className="text-xs">{language === 'en' ? 'Try creating a custom request instead.' : 'Crea una solicitud personalizada.'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {products.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                data-testid={`product-${p.id}`}
                className="rounded-2xl overflow-hidden bg-card border border-border shadow-sm active:scale-[0.98] transition-transform"
              >
                <div className="relative aspect-square">
                  <img
                    src={photoSrc(p.photos && p.photos[0]) || FALLBACK_IMAGE}
                    alt={p.title}
                    className="w-full h-full object-cover"
                  />
                  {p.discount_percent > 0 && (
                    <span className="absolute top-2 left-2 bg-rosa text-white text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1">
                      <Tag size={10} /> -{p.discount_percent}%
                    </span>
                  )}
                </div>
                <div className="p-2.5">
                  <p className="font-semibold text-sm line-clamp-1">{p.title}</p>
                  <p className="text-[10px] text-muted-foreground line-clamp-1 mb-1.5 flex items-center gap-1">
                    <MapPin size={10} /> {p.location?.replaceAll('_', ' ')}
                  </p>
                  <div className="flex items-baseline gap-1 flex-wrap">
                    <span className="font-heading font-bold text-caribbean text-sm">
                      {formatPrice(p.final_price, p.currency || 'USD', { withCode: false })}
                    </span>
                    {p.discount_percent > 0 && (
                      <span className="text-[10px] text-muted-foreground line-through">
                        {formatPrice(p.price, p.currency || 'USD', { withCode: false })}
                      </span>
                    )}
                  </div>
                  {/* Reservation fee teaser (17% + IVA = 19.72%) so the tourist
                      knows they only pay a small upfront amount. */}
                  <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="catalog-reservation-hint">
                    {language === 'en' ? 'Reserve from ' : 'Apártalo desde '}
                    <span className="font-semibold text-foreground">
                      {formatPrice(p.final_price * 0.1972, p.currency || 'USD', { withCode: false })}
                    </span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </MobileShell>
  );
}
